import Vue from 'vue'
import Vuex from 'vuex'
import api from './api'

import * as config from '@/config'

let eventsUrl = params => {
  let url = new URL('/events/list/', config.SERVER_ORIGIN)

  Object
    .entries({limit: config.BATCH_SIZE, offset: 0, ...params})
    .filter(([_, v]) => v != null)
    .forEach(([k, v]) => url.searchParams.set(k, v))
    
  return url
}

let byOrigin = serverOrigin => x =>
  x.context.serverOrigin === serverOrigin

let bySessionId = sessionId => x =>
  x.context.session?.id?.toString() === sessionId

let byUserEmail = userId => x =>
  x.context.user?.username === userId || x.context.user?.email === userId

let byCreated = (a, b) => {
  let [dateA, dateB] = [
    new Date(a.created),
    new Date(b.created)
  ]

  return dateA < dateB ? 1 : -1
}

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    user: JSON.parse(localStorage.getItem('user')) || null,
    requestsInProgress: 0,
    offset: 0,
    events: [],
    eventIds: new Set(),
    origins: []
  },
  getters: {
    isAuthenticated: state => !!state.user,
    getCurrentUser: state => state.user,
    sessionsByOrigin: state => origin => {
      let sessions =
        state.events
          .filter(x =>
            x.context.serverOrigin === origin)
          .filter(x =>
            x.context.session)
          .reduce((acc, x) => {
            acc.add(x.context.session.id)
            return acc
          }, new Set())

      return [...sessions].filter(Boolean)
    },
    filteredEvents: state => ({
      serverOrigin,
      sessionId,
      userEmail
    }) => {
      let opt = (param, f) => param
        ? f(param)
        : Boolean

      return state.events
        .filter(opt(
          serverOrigin, byOrigin))
        .filter(opt(
          sessionId, bySessionId))
        .filter(opt(
          userEmail, byUserEmail))
        .filter(x =>
          x.eventType !== 'console')
        .sort(byCreated)
    },
    sessionConsoleEvents: state => sessionId =>
      state.events
        .filter(bySessionId(sessionId))
        .filter(({eventType}) => eventType === 'console')
  },
  mutations: {
    setUser: (state, user) => {
      state.user = user
      localStorage.setItem('user', JSON.stringify(user))
    },
    clearUser: (state) => {
      state.user = null
      localStorage.removeItem('user')
    },
    fetchStart: state => {
      state.requestsInProgress += 1
    },
    fetchFinish: state => {
      state.requestsInProgress -= 1
    },
    updateOffset: state => {
      state.offset += config.BATCH_SIZE
    },
    addEvent: (state, event) => {
      let duplicate = state.eventIds.has(event._id)

      if (duplicate) return null

      state.eventIds.add(event._id)
      state.events.unshift(event)
      /* extract origin */

      event.context.serverOrigin =
        (event.context.serverOrigin || 'unknown')
          .replace('https://', '')
          .replace('http://', '')
          .replace('/', '')

      let origin = event.context.serverOrigin
      let originExist = state.origins.some(x => x === origin)

      if (!originExist) {
        state.origins.push(origin)
      }
    }
  },
  actions: {
    async login({ commit }, credentials) {
      try {
        const response = await api.post('/api/login', credentials)
        commit('setUser', response.data.user)
        return response
      } catch (error) {
        throw error
      }
    },
    async checkAuthStatus({ commit }) {
      try {
        const response = await api.get('/api/auth-status')
        if (response.data.authenticated && response.data.user) {
          commit('setUser', response.data.user)
        } else {
          commit('clearUser')
        }
        return response.data
      } catch (error) {
        commit('clearUser')
        throw error
      }
    },
    /* Event handling */
    startListener: ({commit}) => {
      let evtSource = config.REALTIME_UPDATES
        ? new EventSource(new URL('/events/subscribe/', config.SERVER_ORIGIN))
        : {}
      evtSource.onmessage = ({data}) =>
        commit('addEvent', JSON.parse(data))
    },
    /* */
    loadEvents: ({commit}, url) => {
      commit('fetchStart')
      return fetch(url)
        .then(res => res.json())
        .then(json => json.forEach(x =>
          commit('addEvent', x)))
        .finally(_ =>
          commit('fetchFinish'))
    },
    /* */
    loadMore: ({dispatch, commit, state: {offset}}) => {
      dispatch('loadEvents', eventsUrl({offset}))
        .then(_ => commit('updateOffset'))
    },
    /* */
    loadFiltered: ({dispatch}, query) => {
      dispatch(
        'loadEvents',
        eventsUrl({...query, limit: 10 * 1000}))
    }
  }
})
